import type { CSSProperties } from "react";
import { formatDate } from "../../api/dates";
import type { LastVisit, SiteRow } from "../../api/sites";
import { StatusChip, type Tone } from "../../components/ui";
import { flagLabel, statusTone } from "./status";

/** One page of the register. A row opens its site; the tick box beside it
 * (office only) selects it for the bulk bar without opening anything. The
 * Red List box puts the site on the list, or takes it off, there and then. */
export default function RegisterTable({
  rows,
  office,
  selected,
  ticked,
  onTick,
  onTickPage,
  onOpen,
  onRedList,
  redListPending,
}: {
  rows: readonly SiteRow[];
  office: boolean;
  selected: number | null;
  ticked: ReadonlySet<number>;
  onTick: (id: number, on: boolean) => void;
  onTickPage: (on: boolean) => void;
  onOpen: (id: number) => void;
  onRedList: (row: SiteRow, on: boolean) => void;
  /** The site whose Red List change is on its way to the server. */
  redListPending: number | null;
}) {
  const pageTicked = rows.length > 0 && rows.every((row) => ticked.has(row.id));
  return (
    <div className="site-table-scroll">
      <table className="data-table site-table">
        <thead>
          <tr>
            {office ? (
              <th className="site-tick">
                <input
                  type="checkbox"
                  checked={pageTicked}
                  onChange={(e) => onTickPage(e.target.checked)}
                  aria-label="Tick every site on this page"
                />
              </th>
            ) : null}
            <th>Site</th>
            <th>Work order no.</th>
            <th>RS no.</th>
            <th className="site-red-col">Red List</th>
            <th>Status</th>
            <th>Valve</th>
            <th>Provider</th>
            <th>Visit</th>
            <th>Needs attention</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <SiteRowView
              key={row.id}
              row={row}
              office={office}
              selected={row.id === selected}
              ticked={ticked.has(row.id)}
              onTick={(on) => onTick(row.id, on)}
              onOpen={() => onOpen(row.id)}
              onRedList={(on) => onRedList(row, on)}
              redListPending={redListPending === row.id}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SiteRowView({
  row,
  office,
  selected,
  ticked,
  onTick,
  onOpen,
  onRedList,
  redListPending,
}: {
  row: SiteRow;
  office: boolean;
  selected: boolean;
  ticked: boolean;
  onTick: (on: boolean) => void;
  onOpen: () => void;
  onRedList: (on: boolean) => void;
  redListPending: boolean;
}) {
  const classes = ["clickable", selected ? "selected" : "", ticked ? "ticked" : ""].filter(Boolean).join(" ");
  return (
    <tr
      className={classes}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target === e.currentTarget) {
          onOpen();
        }
      }}
      tabIndex={0}
      aria-current={selected ? "true" : undefined}
      data-testid={`site-row-${row.id}`}
    >
      {office ? (
        <td className="site-tick" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={ticked} onChange={(e) => onTick(e.target.checked)} aria-label={`Tick ${row.name}`} />
        </td>
      ) : null}
      <td>
        <span className="site-name">{row.name}</span>
        <span className="sub">{row.cluster?.name ?? "No cluster"}</span>
      </td>
      <td className="mono">{row.notif_no ?? "—"}</td>
      <td className="mono">{row.rs_no ?? "—"}</td>
      <td className="site-red-col" onClick={office ? (e) => e.stopPropagation() : undefined}>
        <RedListCell row={row} office={office} pending={redListPending} onChange={onRedList} />
      </td>
      <td>
        <span className="status-dot" style={{ "--dot": statusTone(row.status) } as CSSProperties}>
          {row.status_label}
        </span>
      </td>
      <td className="muted site-nowrap">{valveText(row)}</td>
      <td>{row.provider ? row.provider.name : <em className="site-unassigned">Unassigned</em>}</td>
      <td className="site-nowrap">
        <VisitCell visit={row.last_visit} />
      </td>
      <td>
        <NeedsAttention row={row} />
      </td>
    </tr>
  );
}

/** On the Red List or not, and its rank when it is. The office can tick it on
 * (it joins at the next rank) or off; everyone else reads it. */
function RedListCell({
  row,
  office,
  pending,
  onChange,
}: {
  row: SiteRow;
  office: boolean;
  pending: boolean;
  onChange: (on: boolean) => void;
}) {
  const listed = row.priority !== null;
  const rank = listed ? <span className="site-red-rank">#{row.priority}</span> : null;
  if (!office) {
    return rank ?? <span className="muted">—</span>;
  }
  return (
    <label className="site-red-tick">
      <input
        type="checkbox"
        checked={listed}
        disabled={pending}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={listed ? `Take ${row.name} off the Red List` : `Put ${row.name} on the Red List`}
      />
      {rank}
    </label>
  );
}

/** "Cla-Val · DN150"; either half alone when that is all there is. */
function valveText(row: SiteRow): string {
  const parts = [row.valve_make, row.dn_scope === null ? null : `DN${row.dn_scope}`].filter(
    (part): part is string => part !== null && part !== "",
  );
  return parts.length === 0 ? "—" : parts.join(" · ");
}

const VISIT_LABEL: Record<LastVisit["outcome"], string> = { pending: "Pending", done: "Done", not_done: "Not done" };
const VISIT_TONE: Record<LastVisit["outcome"], Tone> = { pending: "quiet", done: "ok", not_done: "warn" };

/** The site's last planned visit: pending, done or not done, with its date,
 * and the reason on hover when it was not done. A site never planned reads
 * as a dash. */
function VisitCell({ visit }: { visit: LastVisit | null }) {
  if (visit === null) {
    return <span className="muted">—</span>;
  }
  return (
    <span className="site-visit" title={visit.note ?? undefined}>
      <StatusChip tone={VISIT_TONE[visit.outcome]}>{VISIT_LABEL[visit.outcome]}</StatusChip>
      {visit.date ? <span className="muted">{formatDate(visit.date)}</span> : null}
    </span>
  );
}

/** The Red List rank first, since it decides the order of the work, then each
 * open flag (red when it blocks scheduling) and a deferred visit. */
function NeedsAttention({ row }: { row: SiteRow }) {
  if (row.priority === null && row.flags.length === 0 && !row.deferred) {
    return null;
  }
  return (
    <span className="site-attention">
      {row.priority !== null ? <span className="site-red-badge">Red List #{row.priority}</span> : null}
      {row.flags.map((flag) => (
        <span key={flag.id} className={flag.blocking ? "status-chip tone-crit" : "status-chip tone-warn"} title={flag.detail}>
          {flagLabel(flag.code)}
        </span>
      ))}
      {row.deferred ? <span className="status-chip">deferred</span> : null}
    </span>
  );
}
